import { call, select } from 'redux-saga/effects';

import { dispatch } from '@restore/helpers';
import { executePermanentCleanup } from '@restore/measurements/measurements.saga';
import { selectStoreDataRelatedToMeasurements } from '@restore/measurements/measurements.selectors';
import type { MeasurementsState } from '@restore/measurements/measurements.types';
import {
  selectTabCoordinationState,
  selectReadingTaskState,
  selectCurrentSubTaskState,
} from '@restore/readerViewerSession/utils';
import { calcReaderTaskCompleted } from 'src/store-redux/DICOMViewer/transformers';
import {
  MeasurementConfig,
  ScoringFormToolAction,
  type Measurement,
} from '@dynamika-shared/types/measurements';
import { detachRpcScheme } from 'src/services/rpc/detach/scheme';
import { viewerRpcScheme } from 'src/services/rpc/viewer/scheme';
import { measurementsSlice } from '@restore/measurements/measurements.slice';
import type { ViewerToolAction } from '@restore/readerViewerSession/formManagement/formManagement.types';

const {
  showMeasurement,
  hideMeasurement,
  activateMeasurement,
  deactivateMeasurement,
  lockMeasurement,
  unlockMeasurement,
  removeMeasurement,
  screenshotMeasurement,
  screenshotNAMeasurement,
  persistMeasurementUpdate,
} = measurementsSlice.actions;

// `remove-entry` and `retry` are intentionally absent — they are handled
// explicitly below (RPC to the viewer / retry slice action) rather than mapped
// to a legacy measurement slice action. `append` IS mapped: it arms the draw
// tool exactly like `activate` (the append context — `appendToMeasurement` /
// `nextEntry` / `parentMeasurementId` — rides on `data`), so it routes to the
// same `activateMeasurement` slice action. Without this the form's `append`
// command falls through to a no-op and "+ Add annotation" stays stuck on
// "Working…" forever (D7-6906).
type LegacyToolAction = Exclude<
  ScoringFormToolAction,
  'remove-entry' | 'remove-all-entries' | 'retry'
>;
type LegacyActionCreator = (p: MeasurementConfig) => {
  payload: unknown;
  type: string;
};

export const measurementsActions: Record<LegacyToolAction, LegacyActionCreator> = {
  show: showMeasurement,
  hide: hideMeasurement,
  activate: activateMeasurement,
  deactivate: deactivateMeasurement,
  lock: lockMeasurement,
  unlock: unlockMeasurement,
  delete: removeMeasurement,
  screenshot: screenshotMeasurement,
  screenshotNA: screenshotNAMeasurement,
  append: activateMeasurement,
  update: persistMeasurementUpdate,
};

type ToolPayload = ViewerToolAction['payload'];

// D7-6906: single-entry removal — ask the viewer (explicit RPC method) to drop
// the cs annotation by UID. The viewer re-emits a host `removeAnnotation` that
// executeRemove turns into a entry drop. Fire-and-forget: a viewer-side failure
// must not crash this worker (and correctly persists nothing).
function* removeEntry(annotationUID: string) {
  try {
    yield call(viewerRpcScheme.requests.measurement.removeAnnotation, annotationUID);
  } catch (e) {
    console.warn('remove-entry: viewer removeAnnotation request failed', e);
  }
}

// D7-7428: clear EVERY drawn sub-lesion of a split measurement in ONE operation
// (the form's Clear). The old path fired N per-entry `remove-entry` echoes, but
// `takeLatest` on `storeMeasurementWorker` coalesced them, so only the LAST
// sub-lesion actually left the app store — the rest survived and bumped the next
// draw's entry index (TL_01/2, /3…). Here instead:
//   1. drop ALL cornerstone shapes in a single silent viewer round-trip (no
//      per-entry re-persist, so no echo storm and no `takeLatest` coalescing);
//   2. empty the record with ONE PUT — a cleanup PUT for a permanent lesion
//      (keep the immutable record) or a real DELETE for a non-permanent one.
// The canvas UIDs are read from the record's OWN `instrumentStateJson.annotations`,
// so this never depends on the form's (unreliable) first-row UID.
function* removeAllEntries(parentMeasurementId: string) {
  const measurement: Measurement | undefined = yield select(
    (state: { measurements: MeasurementsState }) =>
      state.measurements.data[parentMeasurementId]
  );
  if (!measurement) {
    return;
  }

  const annotationUIDs: string[] = (
    measurement.instrumentStateJson?.annotations ?? []
  )
    .map((a: { annotationUID?: string }) => a.annotationUID)
    .filter((uid: string | undefined): uid is string => Boolean(uid));

  // 1. Canvas — silent bulk removal (one worker loop on the viewer, no persist).
  try {
    yield call(viewerRpcScheme.requests.measurement.removeAllAnnotations, annotationUIDs);
  } catch (e) {
    console.warn('remove-all-entries: viewer bulk clear failed', e);
  }

  // 2. Record + backend — a SINGLE PUT.
  if (measurement.permanent) {
    yield call(executePermanentCleanup, parentMeasurementId);
  } else {
    const {
      task,
      subTask,
    }: ReturnType<typeof selectStoreDataRelatedToMeasurements> = yield select(
      selectStoreDataRelatedToMeasurements
    );
    const clinicalTrialId = task?.clinicalTrialId ?? '';
    const taskId = subTask?.id || task?.id || '';
    yield dispatch(
      measurementsSlice.actions.deleteMeasurement({
        measurementId: parentMeasurementId,
        clinicalTrialId,
        taskId,
      })
    );
  }
}

// Local (non-RPC) tool-action routing: explicit `remove-entry`/`retry` handling
// plus the legacy create/update/lock/etc. actions. `append` maps to
// `activateMeasurement` (see `measurementsActions`) so it arms the draw tool with
// the append context carried on `data`. The RPC form-call correlation metadata
// (__callId / __causationId) is threaded through so the host saga can resolve the
// awaiting promise / build the devtools causation tree.
function* handleLocalToolAction({ action, data, callId, causationId }: ToolPayload) {
  if (action === 'remove-entry') {
    if (data.annotationUID) {
      yield* removeEntry(data.annotationUID);
    }
    return;
  }

  // D7-7428: bulk clear of a split lesion's sub-annotations (Clear). One signal
  // keyed by the record id → silent canvas wipe + a single record-emptying PUT.
  if (action === 'remove-all-entries') {
    if (data.parentMeasurementId) {
      yield* removeAllEntries(data.parentMeasurementId);
    }
    return;
  }

  // D7-6906: re-enqueue a failed append/remove from the per-measurement retry buffer.
  if (action === 'retry') {
    const measurementId = data.parentMeasurementId ?? data.measurementId;
    if (measurementId) {
      yield dispatch(measurementsSlice.actions.retryAppendAnnotation({ measurementId }));
    }
    return;
  }

  const legacy = measurementsActions[action];
  if (legacy) {
    yield dispatch(legacy({ ...data, __callId: callId, __causationId: causationId }));
  }
}

/**
 * Tell the reader when the action they started needs the other window.
 *
 * Plain terms
 * -----------
 * With the form popped out, drawing and screenshots still happen in the window
 * that kept the viewer. If the reader arms a measurement — or asks for a
 * screenshot while that window is in the background — nothing visible happens
 * where they are looking, so this window says where to go.
 *
 * Technical
 * ---------
 * Runs only in the main tab (the detached form addresses `d7-${mainWindowName}`,
 * so every tool action is handled here) and only while a detach session is
 * live; `detachRpcScheme.events.notice` is a no-op until the detached window
 * has introduced itself. The message is composed here rather than shipped as
 * raw visibility state, because this is the side that knows why it is needed.
 */
function* noticeDetachedReader({ action, data }: ToolPayload) {
  const needsVisibleViewer =
    (action === 'lock' && data.needScreenshot) || action === 'screenshot';

  if (needsVisibleViewer && document.visibilityState !== 'visible') {
    yield call(detachRpcScheme.events.notice, {
      message: 'Open the viewer tab to take a screenshot',
    });

    return;
  }

  if (!['activate', 'show'].includes(action) || !data.shouldCreate) {
    return;
  }

  const subTask = yield* selectCurrentSubTaskState();
  const task = yield* selectReadingTaskState();
  const activeTask = subTask ?? task;

  if (calcReaderTaskCompleted(activeTask?.status)) {
    return;
  }

  yield call(detachRpcScheme.events.notice, {
    message: 'Please switch to the viewer tab to take a measurement.',
  });
}

/**
 * Every tool action the scoring form triggers lands here.
 *
 * Attached or detached, the form's `d7.measurement.*` request is addressed to
 * the main tab's peer, so this worker always runs next to the viewer it drives.
 * The detached window only gets told when it has to be looked at.
 */
export function* viewerToolActionWorker({ payload }: ViewerToolAction) {
  const { isDetached } = yield* selectTabCoordinationState();

  if (isDetached) {
    yield* noticeDetachedReader(payload);
  }

  yield* handleLocalToolAction(payload);
}
